/**
 * useAdditionalTableDeltas — bufor delt tabel dodatkowych (Etap 2.2,
 * unifikacja-formatek — wyciągnięcie identycznego wzorca z 6 stron detail
 * dokumentów; pilot: CommercialDocDetail, InternalDocDetail).
 *
 * Jeden wpis Map per tableId, agregowany z każdej instancji
 * `<AdditionalTableTab buffered>` na stronie (strona może mieć kilka
 * zakładek tabel dodatkowych równocześnie). Ten Map jest JEDYNYM źródłem
 * prawdy dla bufora — `AdditionalTableTab` nie ma własnego lokalnego stanu
 * (historia: Krok 7 data-loss bugfix, patrz nagłówek `AdditionalTableTab.tsx`).
 *
 * Hook trzyma DWA niezależne bufory per-tabela: `additionalDeltas` (zapisane/
 * buforowane wiersze — jak wyżej) i `addRowDrafts` (Bug 2 — szkic jeszcze
 * NIEZATWIERDZONEGO wiersza dodawania, mode-independent, patrz JSDoc
 * `AdditionalTableTab.tsx` nagłówek sekcja "Add-row draft"). Szkic celowo NIE
 * wchodzi do `flattenAdditionalDeltas`/`resetAdditionalDeltas` — niezatwierdzony
 * wiersz nigdy nie miał trafić do zapisu, ✓/Enter jest jawnym aktem zatwierdzenia.
 *
 * STALENESS — `getBufferedValue`/`getAddRowDraft` MUSZĄ czytać AKTUALNY stan
 * swojej Mapy przy każdym wywołaniu. Strony przekazują oba gettery TEGO hooka
 * wprost do `buildDocumentAdditionalRenderers(recordId, { getBufferedValue,
 * getAddRowDraft, ... })`, a samo wywołanie fabryki jest CELOWO niememoizowane
 * (odpala się na każdym renderze), żeby callbacki AdditionalTableTab zamykały
 * się na BIEŻĄCEJ Mapie. Gdyby getter był raz zmemoizowaną funkcją z
 * przestarzałym closure, przełączenie zakładek (współdzielony `Tabs`
 * unmountuje nieaktywne panele) odczytałoby po powrocie stary/pusty bufor —
 * to jest właśnie błąd, który Krok 7 naprawił (i który Bug 2 naprawia dla
 * szkicu add-row tym samym wzorcem). `useCallback` z dep `[additionalDeltas]`
 * (odpowiednio `[addRowDrafts]`) gwarantuje NOWĄ funkcję przy każdej zmianie
 * Mapy (a Mapa jest zawsze nowym obiektem — patrz `handleAdditionalDeltasChange`/
 * `handleAddRowDraftChange`), więc nigdy nie zwraca nieaktualnej wartości.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type {
  AdditionalTableAddRowDraft,
  AdditionalTableDeltaSlice,
} from '@/components/additional/AdditionalTableTab';
import type { ParentFieldBindingKey } from '@/hooks/useDictionary';
import {
  additionalFieldsKey,
  type AdditionalParentValues,
  collectParentFieldSlugsForFields,
  fetchAdditionalTableFields,
  pickParentValues,
} from '@/lib/additional-tables/parent-field-values';

/**
 * Jak długo definicje pól tabeli dodatkowej z cache uznajemy przy ZAPISIE za świeże (bez sieci).
 * Krótko, bo definicja pola (wiązania `parent_field`) może zmienić się w ustawieniach w trakcie
 * edycji kartoteki, a zapis z nieaktualnym wiązaniem wysłałby serwerowi złe wartości rodzica.
 */
export const PARENT_FIELDS_SAVE_STALE_MS = 30_000;

/** Spłaszczony bufor delt — kształt body backendu `additionalTables`. */
export interface FlattenedAdditionalTables {
  inserted: AdditionalTableDeltaSlice['inserted'];
  updated: AdditionalTableDeltaSlice['updated'];
  removed: AdditionalTableDeltaSlice['removed'];
}

export function useAdditionalTableDeltas(): {
  additionalDeltas: Map<string, AdditionalTableDeltaSlice>;
  handleAdditionalDeltasChange: (tableId: string, delta: AdditionalTableDeltaSlice) => void;
  getBufferedValue: (tableId: string) => AdditionalTableDeltaSlice | undefined;
  flattenAdditionalDeltas: () => FlattenedAdditionalTables | null;
  buildAdditionalParentValues: (
    ownerValues: Readonly<Record<string, unknown>>,
    parentKey: ParentFieldBindingKey,
  ) => Promise<AdditionalParentValues | null>;
  resetAdditionalDeltas: () => void;
  addRowDrafts: Map<string, AdditionalTableAddRowDraft>;
  handleAddRowDraftChange: (tableId: string, draft: AdditionalTableAddRowDraft) => void;
  getAddRowDraft: (tableId: string) => AdditionalTableAddRowDraft | undefined;
  resetAddRowDrafts: () => void;
} {
  const [additionalDeltas, setAdditionalDeltas] = useState<Map<string, AdditionalTableDeltaSlice>>(
    new Map(),
  );

  const handleAdditionalDeltasChange = useCallback(
    (tableId: string, delta: AdditionalTableDeltaSlice) => {
      setAdditionalDeltas((prev) => {
        const next = new Map(prev);
        next.set(tableId, delta);
        return next;
      });
    },
    [],
  );

  // Patrz komentarz STALENESS w nagłówku pliku — nowa funkcja przy każdej
  // zmianie `additionalDeltas` (Mapa zawsze nowym obiektem), nigdy stale closure.
  const getBufferedValue = useCallback(
    (tableId: string) => additionalDeltas.get(tableId),
    [additionalDeltas],
  );

  // ─── Add-row draft (Bug 2 — lost-on-tab-switch fix) ─────────────────────────
  // Siostrzany bufor do powyższego, ale dla szkicu NIEZATWIERDZONEGO wiersza
  // dodawania — patrz komentarz w nagłówku pliku. Ten sam wzorzec Map-per-tableId
  // + staleness-safe getter.
  const [addRowDrafts, setAddRowDrafts] = useState<Map<string, AdditionalTableAddRowDraft>>(
    new Map(),
  );

  const handleAddRowDraftChange = useCallback(
    (tableId: string, draft: AdditionalTableAddRowDraft) => {
      setAddRowDrafts((prev) => {
        const next = new Map(prev);
        next.set(tableId, draft);
        return next;
      });
    },
    [],
  );

  const getAddRowDraft = useCallback(
    (tableId: string) => addRowDrafts.get(tableId),
    [addRowDrafts],
  );

  /** Czyści szkic add-row po pomyślnym zapisie strony — spójnie z `resetAdditionalDeltas`
   *  (finding [G] pre-commit-verify: usuwa asymetrię, gdzie zatwierdzone wiersze czyściły
   *  się po zapisie, a niezatwierdzony szkic zostawał widoczny mimo udanego zapisu). */
  const resetAddRowDrafts = useCallback(() => {
    setAddRowDrafts(new Map());
  }, []);

  /** Spłaszcza bufor per-tabela do kształtu body backendu (pomija puste tabele). */
  const flattenAdditionalDeltas = useCallback((): FlattenedAdditionalTables | null => {
    const inserted: AdditionalTableDeltaSlice['inserted'] = [];
    const updated: AdditionalTableDeltaSlice['updated'] = [];
    const removed: AdditionalTableDeltaSlice['removed'] = [];
    for (const slice of additionalDeltas.values()) {
      inserted.push(...slice.inserted);
      updated.push(...slice.updated);
      removed.push(...slice.removed);
    }
    if (inserted.length === 0 && updated.length === 0 && removed.length === 0) return null;
    return { inserted, updated, removed };
  }, [additionalDeltas]);

  const queryClient = useQueryClient();

  /**
   * Buduje pole `additionalParentValues` body zapisu kartoteki: wartości pól kartoteki-RODZICA
   * (`ownerValues`: slug → wartość z ekranu, także niezapisane zmiany) dla parametrów słowników
   * z wiązaniem `parent_field` w polach wierszy — serwer waliduje nimi wybrane pozycje słowników.
   *
   * Zakres: tabele z oczekującymi wstawieniami/edycjami wierszy (samo usunięcie wiersza niczego
   * nie waliduje), suma sluggów ze WSZYSTKICH pól tych tabel — wysyłamy tylko to, czego
   * wiązania faktycznie używają, i tylko skalary (nieskalar → `null`; tablica w body kończyłaby
   * się odrzuceniem całego zapisu). `null` = nic do wysłania, pole pomijamy w body.
   *
   * Definicje pól bierzemy z cache zakładki, jeśli jest świeży (młodszy niż
   * `PARENT_FIELDS_SAVE_STALE_MS`) — wtedy bez sieci. Gdy cache nie istnieje albo jest starszy,
   * zapis CZEKA na świeże definicje (`fetchQuery`), więc zmienione wiązania pola trafiają do
   * zapisu, który właśnie idzie. Błąd pobrania przerywa zapis (jawnie), zamiast wysłać zapis bez
   * wartości rodzica. Wołać w funkcji zapisu (`mutationFn`), na wartościach z momentu zapisu.
   */
  const buildAdditionalParentValues = useCallback(
    async (
      ownerValues: Readonly<Record<string, unknown>>,
      parentKey: ParentFieldBindingKey,
    ): Promise<AdditionalParentValues | null> => {
      const tableIds: string[] = [];
      for (const [tableId, slice] of additionalDeltas) {
        if (slice.inserted.length > 0 || slice.updated.length > 0) tableIds.push(tableId);
      }
      if (tableIds.length === 0) return null;

      const fieldsPerTable = await Promise.all(
        tableIds.map((tableId) =>
          queryClient.fetchQuery({
            queryKey: additionalFieldsKey(tableId),
            queryFn: () => fetchAdditionalTableFields(tableId),
            staleTime: PARENT_FIELDS_SAVE_STALE_MS,
          }),
        ),
      );
      const slugs = collectParentFieldSlugsForFields(fieldsPerTable.flat(), parentKey);
      if (slugs.length === 0) return null;
      return pickParentValues(ownerValues, slugs);
    },
    [additionalDeltas, queryClient],
  );

  /** Czyści bufor po pomyślnym atomowym zapisie (backend już zapisał delty). */
  const resetAdditionalDeltas = useCallback(() => {
    setAdditionalDeltas(new Map());
  }, []);

  return {
    additionalDeltas,
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    addRowDrafts,
    handleAddRowDraftChange,
    getAddRowDraft,
    resetAddRowDrafts,
  };
}
